import { EMPTY_ANIMATION_MAP } from '../constants/animationStates'

/**
 * Auto-map animation clip names to critter behavior states.
 *
 * Rules are processed in priority order. For each rule, we search clips
 * for the best match (patterns checked in order). Once a clip is used,
 * it won't be assigned to another rule. This prevents conflicts like
 * "Sit" being claimed by hug when sleep also wants it.
 *
 * Designed for these Unity animation clips:
 * Attack, Bounce, Clicked, Death, Eat, Fear, Fly, Hit,
 * Idle_A, Idle_B, Idle_C, Jump, Lay, Roll, Run, Sit, Spin, Swim, Walk
 */

const MAPPING_RULES = [
  // Idle variants (highest priority - always map these first)
  { key: 'idle_a', patterns: ['idle_a', 'idle a'] },
  { key: 'idle_b', patterns: ['idle_b', 'idle b'] },
  { key: 'idle_c', patterns: ['idle_c', 'idle c'] },

  // Core movement
  { key: 'walk', patterns: ['walk'] },
  { key: 'run', patterns: ['run'] },

  // Direct command mappings (most specific first)
  { key: 'eat', patterns: ['eat', 'feed'] },
  { key: 'pet', patterns: ['bounce', 'pet', 'pat', 'happy', 'clicked'] },
  { key: 'dance', patterns: ['spin', 'dance', 'splash'] },
  { key: 'trick', patterns: ['roll', 'trick', 'flip'] },
  { key: 'bonk', patterns: ['hit', 'bonk', 'attack'] },
  { key: 'hug', patterns: ['sit', 'hug', 'love'] },

  // Lifecycle (use remaining clips)
  { key: 'sleep', patterns: ['lay', 'sleep', 'rest', 'sit'] },
  { key: 'spawn', patterns: ['jump', 'spawn', 'appear'] },
  { key: 'despawn', patterns: ['death', 'despawn', 'die', 'disappear', 'fear'] },
]

export function autoMapAnimations(clipNames) {
  const newMap = { ...EMPTY_ANIMATION_MAP }
  const usedClips = new Set()

  // For each rule (in priority order), find the best matching clip
  for (const rule of MAPPING_RULES) {
    for (const pattern of rule.patterns) {
      const match = clipNames.find(
        (name) => name.toLowerCase().includes(pattern) && !usedClips.has(name)
      )
      if (match) {
        newMap[rule.key] = match
        usedClips.add(match)
        break
      }
    }
  }

  // If only one clip exists, map it to idle_a
  if (clipNames.length === 1 && !newMap.idle_a) {
    newMap.idle_a = clipNames[0]
  }

  return newMap
}
